import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

const tabs = [
  { name: "Discover", path: "/" },
  { name: "My Purchase", path: "/student/purchased" },
  { name: "Expert", path: "/expert" },
];

const marketTabs = [
  { name: "Index", category: "Index-Analyst" },
  { name: "Stock", category: "Stock-Analyst" },
  { name: "Swing-Trades", category: "Swing-Trades" },
];

const MobileNavbar = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const teacher = useSelector((state) => state.student.teachers);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location.pathname);
  const [tabWidth, setTabWidth] = useState(80);
  const [tabOffset, setTabOffset] = useState(0);

  const showTabs = [
    "/",
    "/student/purchased",
    "/expert",
    "/student/cart",
  ].includes(location.pathname);
  const showMarketTabs = location.pathname.startsWith("/market");

  // Extract category from URL (for market tabs)
  const queryParams = new URLSearchParams(location.search);
  const selectedCategory = queryParams.get("category") || "Index-Analyst"; // Default category

  // Find the active market tab index
  const marketActiveIndex = marketTabs.findIndex(
    (tab) => tab.category === selectedCategory
  );

  // Handle tab indicator movement
  useEffect(() => {
    const tabElements = showMarketTabs
      ? document.querySelectorAll(".market-tab")
      : document.querySelectorAll(".tab");
    const currentIndex = showMarketTabs
      ? marketActiveIndex
      : tabs.findIndex((t) => t.path === location.pathname);
    if (tabElements[currentIndex]) {
      setTabWidth(tabElements[currentIndex].offsetWidth);
      setTabOffset(tabElements[currentIndex].offsetLeft);
    }
  }, [location.pathname, marketActiveIndex, showMarketTabs]);

  // Handle Market Tab Click
  const handleMarketTabClick = (index) => {
    const category = marketTabs[index].category;
    navigate(`/market?category=${category}`);
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        width: "100%",
        bgcolor: "white",
        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
        borderBottomLeftRadius: "16px",
        borderBottomRightRadius: "16px",
        overflow: "hidden",
        zIndex: "1000",
      }}
    >
      {/* Top Buttons */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1}
      >
        <Button
          onClick={() => setOpenDrawer(!openDrawer)}
          sx={{
            borderRadius: "20px",
            bgcolor: "#EEEEEE",
            px: 2,
            py: 0.5,
            display: "flex",
            alignItems: "center",
          }}
        >
          <GridViewIcon sx={{ mr: 1, color: "black" }} />
          <Typography
            sx={{
              fontWeight: "bold",
              color: "#707070",
              textTransform: "capitalize",
            }}
          >
            Explore
          </Typography>
        </Button>

        <Box>
          {/* <a href="/gsm.apk" style={{ paddingRight: "0.5rem" }}>
            <DownloadOutlinedIcon sx={{ color: "blue" }} />
          </a> */}
          <Button
            sx={{ borderRadius: "20px", bgcolor: "#EEEEEE", px: 2, py: 0.5 }}
            onClick={() => navigate("/contact-us")}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: "black",
                textTransform: "capitalize",
              }}
            >
              FAQ
            </Typography>
          </Button>
        </Box>
      </Grid>

      {/* Home Tabs */}
      {showTabs && (
        <>
          <Grid container justifyContent="space-around" mt={1} mb={2}>
            {tabs.map((tab, index) => (
              <Box
                key={tab.name}
                className="tab"
                sx={{
                  textAlign: "center",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => navigate(tab.path)}
              >
                <Typography
                  sx={{
                    color:
                      location.pathname === tab.path ? "#6D0283" : "#440142",
                    fontWeight:
                      location.pathname === tab.path ? "bold" : "normal",
                    textAlign: "center",
                  }}
                >
                  {tab.name}
                </Typography>
              </Box>
            ))}
          </Grid>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: tabOffset,
              height: "5px",
              width: tabWidth,
              bgcolor: "#6D0283",
              transition: "left 0.3s ease-in-out, width 0.3s ease-in-out",
              borderRadius: "2px",
            }}
          />
        </>
      )}

      {/* Market Page Tabs */}
      {showMarketTabs && (
        <>
          <Grid container justifyContent="space-around" mt={1} mb={2}>
            {marketTabs.map((tab, index) => (
              <Box
                key={tab.name}
                className="market-tab"
                sx={{
                  textAlign: "center",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => handleMarketTabClick(index)}
              >
                <Typography
                  sx={{
                    color: marketActiveIndex === index ? "#6D0283" : "#440142",
                    fontWeight: marketActiveIndex === index ? "bold" : "normal",
                    textAlign: "center",
                  }}
                >
                  {tab.name}
                </Typography>
              </Box>
            ))}
          </Grid>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: tabOffset,
              height: "5px",
              width: tabWidth,
              bgcolor: "#6D0283",
              transition: "left 0.3s ease-in-out, width 0.3s ease-in-out",
              borderRadius: "2px",
            }}
          />
        </>
      )}

      <SwipeableDrawer
        anchor="left"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onOpen={() => setOpenDrawer(true)}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "#f4f5f7",
            width: "300px",
            padding: "16px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <Box
          role="presentation"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
          onClick={() => setOpenDrawer(!openDrawer)}
          onKeyDown={() => setOpenDrawer(!openDrawer)}
        >
          <Typography
            textAlign="center"
            sx={{
              mb: 3,
              fontWeight: "bold",
              fontSize: "1.2rem",
              color: "#3F0A34",
              letterSpacing: "1px",
            }}
          >
            Meet Our Teachers
          </Typography>

          <List sx={{ width: "100%" }}>
            {teacher?.map((teacher) => (
              <ListItem
                key={teacher?._id}
                button
                sx={{ display: "flex", gap: 2, mb: 2 }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={teacher?.picture}
                    alt={teacher?.firstName}
                    sx={{ width: 50, height: 50 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={teacher?.firstName + " " + teacher?.lastName}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </SwipeableDrawer>
    </Box>
  );
};

export default MobileNavbar;
